/**
 * Canonical status → semantic tone mapping.
 *
 * The audit found the SAME status rendered in different colours on list vs detail
 * pages (e.g. APPROVED = blue on the orders list, cyan on the detail). This is the
 * single source of truth so `StatusBadge` (and any legacy antd Tag during migration)
 * colour a status identically everywhere. Explicit codes win; a keyword heuristic
 * covers the long tail so new/unlisted statuses still get a sensible colour.
 */
export type StatusTone = 'neutral' | 'info' | 'processing' | 'success' | 'warning' | 'danger' | 'accent';

// Explicit, known domain vocabularies (orders, billing, complaints, returns, lifecycle).
const MAP: Record<string, StatusTone> = {
  // Orders
  DRAFT: 'neutral', PLACED: 'info', ON_HOLD: 'warning', CORRECTION_REQUIRED: 'warning',
  APPROVED: 'info', ACCEPTED: 'processing', AWAITING_TRANSFER: 'info',
  AWAITING_PRODUCTION: 'processing', IN_PRODUCTION: 'processing', BACKORDER: 'warning',
  PACKED: 'accent', DISPATCHED: 'accent', DELIVERED: 'success', INVOICED: 'processing',
  CLOSED: 'neutral', CANCELLED: 'danger', REJECTED: 'danger',
  // Payments / invoices
  PAID: 'success', PARTIALLY_PAID: 'warning', UNPAID: 'danger', PENDING: 'warning',
  VERIFIED: 'success', OVERDUE: 'danger', REFUNDED: 'neutral',
  // Complaints. AWAITING_INFO is NEUTRAL, not a warning: the SLA clock is stopped and the ball is
  // with the customer, so it is not a queue anyone is late on. REOPENED means a Super Admin sent a
  // closed complaint back for review.
  OPEN: 'info', IN_PROGRESS: 'processing', RESOLVED: 'success', ESCALATED: 'danger',
  AWAITING_INFO: 'neutral', REOPENED: 'accent',
  // Returns / credit notes
  REQUESTED: 'info', RECEIVED: 'processing', CREDITED: 'success', REVERSED: 'neutral',
  // Generic lifecycle
  ACTIVE: 'success', INACTIVE: 'neutral', ARCHIVED: 'neutral', EXPIRED: 'danger', BLOCKED: 'danger',
  // A ledger side: owed to us is the normal state (plain); a credit in the dealer's favour is green.
  DR: 'neutral', CR: 'success',
  SUBMITTED: 'info', APPROVED_PENDING: 'warning', COMPLETED: 'success',
};

const KEYWORDS: [RegExp, StatusTone][] = [
  [/CANCEL|REJECT|FAIL|VOID|OVERDUE|EXPIR|BLOCK|DECLIN|ERROR/, 'danger'],
  [/HOLD|PEND|AWAIT|CORRECTION|PARTIAL|WARN|BACKORDER|DUE/, 'warning'],
  [/APPROV|DELIVER|COMPLET|RESOLV|PAID|VERIFI|ACTIVE|CREDIT|SUCCESS|DONE/, 'success'],
  [/PROGRESS|PROCESS|PACK|DISPATCH|TRANSIT|PRODUCTION|SHIPP/, 'processing'],
  [/DRAFT|CLOSED|INACTIVE|ARCHIV|NEUTRAL|CANCELLED_OK/, 'neutral'],
];

/** Map a status code to its semantic tone. Case-insensitive; falls back to `info`. */
export function statusTone(status?: string | null): StatusTone {
  if (!status) return 'neutral';
  const s = status.toUpperCase();
  if (MAP[s]) return MAP[s];
  for (const [re, tone] of KEYWORDS) if (re.test(s)) return tone;
  return 'info';
}

/** Human label for a status code (UNDERSCORE_CASE → "underscore case"). Casing left
 *  to the caller/CSS so this never changes a page's existing upper/lower rendering. */
export function humanizeStatus(status?: string | null): string {
  return (status ?? '').replace(/_/g, ' ').trim();
}

/**
 * ORDER statuses in plain English.
 *
 * The stored code `ACCEPTED` sat one step after `APPROVED` and nobody could tell the two apart:
 * both are past participles meaning "somebody said yes", and the screen never said WHICH somebody.
 * They are two different acts by two different people — the office APPROVES the order commercially,
 * and then the warehouse manager takes it on and starts work. So the second one is now named for
 * what is actually happening to the goods: IN FULFILLMENT.
 *
 * Display only. `orders.status` still stores `ACCEPTED`, and so do the API contract, the state
 * machine, the reports and the Android app — renaming the stored value would be a migration across
 * three codebases to fix a wording problem.
 *
 * Deliberately NOT folded into `humanizeStatus`: `ACCEPTED` is also a COMPLAINT resolution ("the
 * customer accepted the fix") and a rep-cash custody state ("the accountant accepted the cash"),
 * where "In fulfillment" would be nonsense. Only order surfaces call this. Same reasoning as
 * `retailerStatusLabel` below.
 *
 * Every other order status is already plain, so the map carries only the one that needs rewording;
 * anything missing falls through to the humanized code and keeps working.
 *
 * UPPER CASE on purpose. These labels land in the steps strip, the header pill, the status column,
 * the timeline and the filter dropdown, all of which render the bare code today — so a title-cased
 * entry would come out as "PLACED · APPROVED · In fulfillment · PACKED" and look like a bug. Casing
 * stays the caller's business, exactly as `humanizeStatus` leaves it. Prose surfaces that want
 * sentence case (the order page's status banner, the WHM pipeline list) write their own string.
 */
const ORDER_STATUS_LABEL: Record<string, string> = {
  ACCEPTED: 'IN FULFILLMENT',
};

/** Human label for an ORDER status code. Unknown codes degrade to the humanized code. */
export function orderStatusLabel(status?: string | null): string {
  if (!status) return '';
  return ORDER_STATUS_LABEL[status.toUpperCase()] ?? humanizeStatus(status);
}

/**
 * What one row of an order's status timeline records.
 *
 * A shipment's dispatch or delivery writes a row even when the ORDER does not move — a part-shipped
 * order stays PACKED. Since DEF-062 such a row keeps the order's real statuses (PACKED → PACKED) and
 * names the shipment's step in `eventType`; rows written before that have no from-status and the
 * step as their to-status. Either way: `step` is the status word to show for the row, and
 * `partialShipment` says the order did not move.
 */
export function orderTimelineStep(t: { fromStatus?: string | null; toStatus: string; eventType?: string | null }): { step: string; partialShipment: boolean } {
  const shipStep = t.eventType === 'SHIPMENT_DISPATCHED' ? 'DISPATCHED' : t.eventType === 'SHIPMENT_DELIVERED' ? 'DELIVERED' : null;
  if (shipStep) {
    const partialShipment = t.fromStatus === t.toStatus;
    return { step: partialShipment ? shipStep : t.toStatus, partialShipment };
  }
  const partialShipment = !t.fromStatus && (t.toStatus === 'DISPATCHED' || t.toStatus === 'DELIVERED');
  return { step: t.toStatus, partialShipment };
}

/**
 * Retailer lifecycle statuses in plain English.
 *
 * The stored codes are trade jargon. "PROSPECT" in particular is a sales term for a lead, and
 * readers of the dealer register kept reading it as a condition the dealer is IN rather than a
 * stage they have not yet come out of — so the register was labelling 32 dealers with a word that
 * told nobody what to do about them.
 *
 * Display only: the DB values are untouched, so filters, imports and the API contract are
 * unaffected. Deliberately NOT folded into `humanizeStatus`, because `PROSPECT` also names a VISIT
 * TYPE — a call on someone who is not yet a customer — where "Onboarding pending" would be wrong.
 */
const RETAILER_STATUS_LABEL: Record<string, string> = {
  PROSPECT: 'Onboarding pending',
  KYC_PENDING: 'KYC pending',
  ACTIVE: 'Active',
  SUSPENDED: 'Suspended',
  CLOSED: 'Closed',
};

/** Human label for a RETAILER status code. Unknown codes degrade to the humanized code. */
export function retailerStatusLabel(status?: string | null): string {
  if (!status) return '';
  return RETAILER_STATUS_LABEL[status.toUpperCase()] ?? humanizeStatus(status);
}
